import {useEffect, useState} from 'react';

import {APIError} from 'data/errors';
import {useHostName} from 'presentation/hooks/useHostName';
import {IFormData} from 'presentation/pages/public/sign-up/components/Forms/Step1/schema';
import {clearLoginMask, getLoginIdentifier} from 'presentation/utils/masks';
import {OperationStateType, OperationStateTypesEnum} from 'presentation/constants';
import {translations} from 'presentation/translations-new';
import {formatApiErrorResponse} from 'presentation/utils/formatApiErrorResponse';
import {HttpStatusCode} from 'data/protocols/http';
import {APIErrorsMessage} from '../../constants/APIErrorsMessage';
import {PublicFlowEnum} from '../../constants/PublicFlowEnum';
import {signUpCheckExistence} from '../../services/signUpCheckExistence';
import {useSignUpFormDataStore} from '../../sign-up/store/useSignUpFormDataStore';
import {useSteps} from '../useSteps';
import {UseCheckValidUserProps} from './types';

const {serverError} = translations['pt-br'].commons;
const {alreadyExists, notFound, invalidUserOrPassword} = translations['pt-br'].publicFlow.APIErrors;

export function useCheckValidUser({methods}: UseCheckValidUserProps) {
	const {
		dataHostNameState: {productCode, productName},
	} = useHostName();
	const {
		watch,
		getFieldState,
		formState: {isValidating},
	} = methods;

	const [requestState, setRequestState] = useState<OperationStateType | null>(null);
	const [apiErrorMsg, setApiErrorMsg] = useState('');
	const {handleNextStep} = useSteps(PublicFlowEnum.SIGN_UP);
	const setSignUpFormData = useSignUpFormDataStore(state => state.setSignUpFormData);

	const loginData = watch('login');
	const {isDirty} = getFieldState('login');
	const identifier = getLoginIdentifier(clearLoginMask(loginData));
	const showAPIError = requestState === OperationStateTypesEnum.ERROR && isDirty;

	const handleCheckValidUser = async ({login}: IFormData) => {
		try {
			setRequestState(OperationStateTypesEnum.PENDING);
			if (identifier === '0') {
				setRequestState(OperationStateTypesEnum.ERROR);
				setApiErrorMsg(invalidUserOrPassword);
				return;
			}

			const response = await signUpCheckExistence({
				cd_usuario: identifier === '2' ? login : '',
				cpf: identifier === '1' ? login : '',
				cdEmpresaPlano: productCode.toString(),
			});
			const parsedResponse = JSON.parse(response.detail);

			if (!parsedResponse.existeCadastro.success) {
				const errorMsg =
					parsedResponse.existeCadastro.msg === APIErrorsMessage.ALREADY_EXISTS
						? alreadyExists
						: `${notFound + productName}.`;
				setRequestState(OperationStateTypesEnum.ERROR);
				setApiErrorMsg(errorMsg);
			} else {
				setRequestState(OperationStateTypesEnum.DONE);
				setSignUpFormData(state => ({
					...state,
					cdUsuario: identifier === '2' ? login : '',
					cpf: identifier === '1' ? login : '',
					cdEmpresaPlano: productCode,
					identificador: identifier,
				}));
				handleNextStep();
			}
		} catch (error) {
			if (error instanceof APIError) {
				setRequestState(OperationStateTypesEnum.ERROR);
				const parsedResponse = formatApiErrorResponse(error);
				setApiErrorMsg(parsedResponse);

				if (error.statusCode === HttpStatusCode.unprocessable) {
					setApiErrorMsg(`${notFound + productName}.`);
				}

				if (error.statusCode === HttpStatusCode.serverError) {
					setApiErrorMsg(serverError);
				}
			}
		}
	};

	const clearAPIError = () => {
		setRequestState(null);
	};

	useEffect(() => {
		if (isValidating) {
			clearAPIError();
		}
	}, [isValidating]);

	return {
		handleCheckValidUser,
		apiErrorMsg,
		showAPIError,
	};
}
